import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { Barn, Silo } from '../../models/barn.model';
import * as barnActions from '../../store/actions/barn.actions';
import { getBarns, getSilos } from '../../store/selectors/barn.selector';

export interface BarnDialogInputData extends AbstractFormDialogData {
  barn: Barn;
}

export interface BarnDialogResultData {
  barn: Barn;
}

@Component({
  selector: 'app-barn-item',
  templateUrl: 'barn-item.component.html',
  styles: [
    `
      .form-field-suffix {
        padding-left: 10px;
      }
    `,
  ],
  standalone: false,
})
export class BarnItemComponent
  extends AbstractFormDialog<BarnDialogResultData>
  implements OnDestroy
{
  formGroup = new FormGroup({
    name: new FormControl<string>(null, [Validators.required]),
    surfaceArea: new FormControl<number>(null, [Validators.required]),
    silo: new FormControl<Silo[]>(undefined),
  });
  subscription = new Subscription();
  silos$ = this.store.select(getSilos);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BarnDialogInputData,
    protected mdDialogRef: MatDialogRef<
      BarnItemComponent,
      AbstractDialogResultData<BarnDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private locale: LocaleService,
  ) {
    super();
    this.data.title = this.locale.translate('settings.barn-item.title-create');

    this.silos$.pipe(take(1)).subscribe((silos: Silo[]) => {
      if (!!this.data.barn && !!this.data.barn.id) {
        this.data.title = `${this.locale.translate('settings.barn-item.title-edit')} ${
          this.data.barn.name
        } `;
        this.formGroup.controls.name.setValue(this.data.barn.name);
        this.formGroup.controls.surfaceArea.setValue(this.data.barn.surfaceArea);
        this.formGroup.controls.silo.setValue(
          this.data.barn.properties?.silos?.map((selectedSilo) =>
            silos.find((s) => s.id === selectedSilo.id),
          ),
        );
      }
    });

    this.subscription.add(
      this.formGroup.controls.name.valueChanges.subscribe((name: string) => {
        this.store
          .select(getBarns)
          .pipe(take(1))
          .subscribe((barns) => {
            if (barns) {
              if (barns.findIndex((b) => b.name === name && b.id !== this.data.barn.id) !== -1) {
                this.formGroup.controls.name.setErrors({ duplicateName: true });
                this.formGroup.controls.name.markAsTouched();
              } else {
                this.formGroup.controls.name.setErrors(null);
                this.formGroup.controls.name.markAsPristine();
              }
            }
          });
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  save() {
    if (this.formGroup.valid) {
      const barn = <Barn>{
        id: this.data.barn ? this.data.barn.id : undefined,
        name: <string>this.formGroup.controls.name.value,
        surfaceArea: this.formGroup.controls.surfaceArea.value,
        properties: {
          silos: this.formGroup.controls.silo.value,
        },
      };
      this.store.dispatch(barnActions.upsertBarn({ barn }));
      this.confirm({ barn });
    }
  }
}
